/**
 * View model for the **pratique** step (S1b / S2b / S3): the groups the run
 * walks through, what the hand is doing while it walks them, and the
 * sentences that name it.
 *
 * Pure, like `grip.ts`, so the component stays a renderer and the wording —
 * the part that decides whether the step teaches anything — is testable.
 *
 * ## The hand of a note is a fact about an instant, not about a pitch
 *
 * S3 colours each key by hand, and the copy tells the player the colour *is*
 * the instruction. The hand was resolved by looking the midi up in the whole
 * unit's right-hand set, so a Do3 played by the left hand was painted
 * right-hand as soon as the right hand touched a Do3 anywhere else in the
 * unit. Measured on PDMX 450: 31.8% of two-hand units contain at least one
 * midi played by both hands at different instants, and 4.4% of all (tick,
 * midi) pairs were coloured with the wrong hand (12 617 of 285 655).
 *
 * A colour code taught and then broken is worse than no colour code, so the
 * hand travels with the note from the moment the group is built and is never
 * inferred again.
 *
 * ## The grip is a landmark here, never a unit
 *
 * S1a segmented the unit into occupations; this step re-segments it into
 * attacks, and that is the right grain — an attack is what the hand does,
 * what the roll shows, and what S4 will time. Sub-dividing the run by grip
 * was measured and rejected: median 1 attack per grip on the left hand, 2 on
 * the right, with 51.3% of grips carrying a single attack. That is the
 * note-by-note discovery in disguise, the same trap the grip step avoided.
 *
 * So a grip appears here as an **annotation**: the opening line that hands
 * back the configuration the player just installed, the fingering carried on
 * the keys it certifies, and a marker where the hand actually moves. 70.3% of
 * left-hand attacks and 66.3% of right-hand ones fall inside a grip the grip
 * step made the player execute, and 76.9% of left-hand runs open on one — the
 * link is worth stating because it is true three times out of four, and
 * silent for the rest.
 *
 * ## A move is a grip that shares no key with the one before it
 *
 * Not every grip boundary is a displacement: a grip closes as soon as one
 * finger changes key, which happens constantly without the hand going
 * anywhere. Announcing those would be noise. A real move — no key in common —
 * has a median of 1 per run, P90 of 4, and is legitimately absent from 42.6%
 * of left-hand runs. That is what this step is *for*, and it was never named.
 */

import { placeSpelling } from './grip';

import {
  phraseHandShapePlan,
  shapePresses,
} from '@/lib/learning-journey/hand-shapes';
import type { Phrase, PhraseGrip, PhraseNote } from '@/lib/learning-journey/types';

export type PracticeVariant = 'S1b' | 'S2b' | 'S3';
export type PracticeHand = 'L' | 'R';

// ── Groups ────────────────────────────────────────────────────────

/** One key of a group, with the hand that actually plays it at this instant. */
export type PracticeNoteView = {
  readonly midi: number;
  readonly hand: PracticeHand;
};

/** One attack instant: the keys struck together, and when. */
export type PracticeGroup = {
  /** Ascending by pitch, deduplicated. */
  readonly notes: readonly PracticeNoteView[];
  /** The same keys, for the matcher and the hint layer. */
  readonly midis: readonly number[];
  readonly startTick: number;
};

/** The hand a variant works, or `null` for both. */
export function variantHand(variant: PracticeVariant): 'left' | 'right' | null {
  if (variant === 'S1b') {
    return 'left';
  }

  if (variant === 'S2b') {
    return 'right';
  }

  return null;
}

/**
 * The run's groups, in order, each carrying the hand of every key.
 *
 * A unison — both hands on the same key at the same tick — is a genuine
 * simultaneity and not a mis-attribution, so one of the two has to win. The
 * right hand does, the same convention S0 uses when it colours a chord.
 */
export function buildPracticeGroups(
  phrase: Pick<Phrase, 'notesByHand'>,
  variant: PracticeVariant,
): readonly PracticeGroup[] {
  const tagged: { note: PhraseNote; hand: PracticeHand }[] = [];
  const hand = variantHand(variant);

  if (hand !== 'right') {
    for (const note of phrase.notesByHand.left) {
      tagged.push({ note, hand: 'L' });
    }
  }

  if (hand !== 'left') {
    for (const note of phrase.notesByHand.right) {
      tagged.push({ note, hand: 'R' });
    }
  }

  if (tagged.length === 0) {
    return [];
  }

  tagged.sort((a, b) => a.note.startTick - b.note.startTick);

  const groups: { startTick: number; byMidi: Map<number, PracticeHand> }[] = [];
  let current: { startTick: number; byMidi: Map<number, PracticeHand> } | null =
    null;

  for (const { note, hand: noteHand } of tagged) {
    if (current === null || note.startTick !== current.startTick) {
      current = { startTick: note.startTick, byMidi: new Map() };
      groups.push(current);
    }

    if (noteHand === 'R' || !current.byMidi.has(note.midi)) {
      current.byMidi.set(note.midi, noteHand);
    }
  }

  return groups.map((g) => {
    const notes = [...g.byMidi.entries()]
      .map(([midi, noteHand]) => ({ midi, hand: noteHand }))
      .sort((a, b) => a.midi - b.midi);

    return {
      notes,
      midis: notes.map((n) => n.midi),
      startTick: g.startTick,
    };
  });
}

/**
 * Where the roll goes once the last attack of a pass is played, or `null`.
 *
 * Every other attack scrolls the roll on to the next one, and that movement
 * is what tells the player the key counted. The last one had no next, so the
 * roll stayed put under « Passage réussi » and the one confirmation the run
 * gives by motion was missing exactly where the pass ends.
 *
 * So it goes to where the last attack's sound ends — the same « on to what
 * follows » as the others. One tick short of the unit's end at most: the
 * roll resting on the next unit's first tick is read as the player having
 * scrolled away to it.
 */
export function passEndTick(
  phrase: Pick<Phrase, 'notesByHand' | 'endTick'>,
  groups: readonly PracticeGroup[],
): number | null {
  const last = groups[groups.length - 1];

  if (last === undefined) {
    return null;
  }

  let end: number | null = null;

  for (const note of [...phrase.notesByHand.left, ...phrase.notesByHand.right]) {
    if (note.startTick !== last.startTick || !last.midis.includes(note.midi)) {
      continue;
    }

    end = end === null ? note.endTick : Math.max(end, note.endTick);
  }

  if (end === null) {
    return null;
  }

  return Math.max(last.startTick, Math.min(end, phrase.endTick - 1));
}

/** True when the group is struck by both hands at once. */
export function isMixedHands(group: PracticeGroup): boolean {
  return (
    group.notes.some((n) => n.hand === 'L') &&
    group.notes.some((n) => n.hand === 'R')
  );
}

/** The lone hand a group belongs to, or `null` when both play it. */
export function soleHand(group: PracticeGroup): PracticeHand | null {
  if (group.notes.length === 0 || isMixedHands(group)) {
    return null;
  }

  return group.notes[0].hand;
}

// ── What each hand is doing ───────────────────────────────────────

/** A key sounding at a group's instant that the group does not strike. */
export type HeldKey = {
  readonly midi: number;
  readonly hand: PracticeHand;
};

/**
 * What the two hands are doing at one group.
 *
 * Ternary and not binary, because the binary reading was measured wrong.
 * Over 136 178 groups of two-hand units: **46.0%** have both hands striking,
 * **39.9%** have one hand striking while the other *holds*, and only
 * **14.0%** have one hand striking with the other truly silent.
 *
 * So "Main gauche seule" — the line this replaces — is false in 74% of the
 * groups it was shown on: it tells a hand it has nothing to do when its
 * actual instruction is *do not let go*. And it said so while the roll
 * displayed the held key, which is the panel-versus-roll divergence the
 * journey treats as a bug everywhere else.
 */
export type GroupTurn =
  | { readonly kind: 'both' }
  | {
      readonly kind: 'one-holds';
      readonly playing: PracticeHand;
      readonly holding: PracticeHand;
    }
  | { readonly kind: 'one-alone'; readonly playing: PracticeHand };

/**
 * Keys still sounding at the group's instant that the group does not ask
 * for, with the hand that is holding them.
 *
 * A fact about the score, which is why it lives here and not in the view.
 * It was computed in the component and sent to the roll only — so the panel
 * painted a key white and at rest while the player's finger was physically
 * holding it down, and the roll lit the same key at full strength. Measured:
 * 48.0% of groups carry at least one, and **78.3%** of those keys belong to
 * the *other* hand, which makes them the instruction rather than noise.
 */
export function heldKeysAt(
  phrase: Phrase,
  group: PracticeGroup,
): readonly HeldKey[] {
  const struck = new Set(group.midis);
  const seen = new Set<number>();
  const out: HeldKey[] = [];

  const sides: readonly (readonly [PracticeHand, readonly PhraseNote[]])[] = [
    ['L', phrase.notesByHand.left],
    ['R', phrase.notesByHand.right],
  ];

  for (const [hand, notes] of sides) {
    for (const note of notes) {
      if (note.startTick > group.startTick || note.endTick <= group.startTick) {
        continue;
      }

      if (struck.has(note.midi) || seen.has(note.midi)) {
        continue;
      }

      seen.add(note.midi);
      out.push({ midi: note.midi, hand });
    }
  }

  return out.sort((a, b) => a.midi - b.midi);
}

/**
 * Per group, the hands with a key **down** that this group does not strike.
 *
 * Same fact as `heldKeysAt`, folded to hands and computed for a whole run at
 * once, because the run rail needs it per cell rather than per key.
 *
 * The rail draws one cell per group, and a group is the *union* of both
 * hands' attacks. A hand holding a semibreve under a hand playing quavers
 * therefore had its bar carved into four cells and lit only the first: four
 * held chords showed as four specks, on the step whose instruction to that
 * hand is precisely *do not let go*. Struck and held are one state for the
 * key and for the finger; the rail is the only place that read them apart.
 *
 * A unison counts as held for the hand that lost it: `buildPracticeGroups`
 * awards a key struck by both hands at once to the right hand, so the left
 * one is not in `notes` while its finger is very much down.
 */
export function sustainByGroup(
  phrase: Pick<Phrase, 'notesByHand'>,
  groups: readonly PracticeGroup[],
): readonly (readonly PracticeHand[])[] {
  const sides: readonly (readonly [PracticeHand, readonly PhraseNote[]])[] = [
    ['L', phrase.notesByHand.left],
    ['R', phrase.notesByHand.right],
  ];

  return groups.map((group) => {
    const struck = new Set(group.notes.map((n) => n.hand));
    const out: PracticeHand[] = [];

    for (const [hand, notes] of sides) {
      if (struck.has(hand)) {
        continue;
      }

      const sounding = notes.some(
        (n) => n.startTick <= group.startTick && n.endTick > group.startTick,
      );

      if (sounding) {
        out.push(hand);
      }
    }

    return out;
  });
}

/** The ternary reading of a group. */
export function groupTurn(
  group: PracticeGroup,
  held: readonly HeldKey[],
): GroupTurn {
  const playing = soleHand(group);

  if (playing === null) {
    return { kind: 'both' };
  }

  const holding: PracticeHand = playing === 'L' ? 'R' : 'L';

  if (held.some((k) => k.hand === holding)) {
    return { kind: 'one-holds', playing, holding };
  }

  return { kind: 'one-alone', playing };
}

const HAND_WORD: Readonly<Record<PracticeHand, string>> = {
  L: 'gauche',
  R: 'droite',
};

/** The line that says whose turn it is, derived from the group itself. */
export function turnLine(turn: GroupTurn): string {
  if (turn.kind === 'both') {
    return 'Les deux mains ensemble.';
  }

  if (turn.kind === 'one-holds') {
    return `Main ${HAND_WORD[turn.playing]} seule, la ${HAND_WORD[turn.holding]} garde ses touches.`;
  }

  return `Main ${HAND_WORD[turn.playing]} seule.`;
}

// ── Grips as landmarks ────────────────────────────────────────────

/** The grip covering `tick`, or `null` outside every occupation. */
export function gripAt(
  grips: readonly PhraseGrip[],
  tick: number,
): PhraseGrip | null {
  for (const grip of grips) {
    if (grip.startTick <= tick && tick < grip.endTick) {
      return grip;
    }
  }

  return null;
}

/**
 * Fingers on one key at one instant, ascending, or empty.
 *
 * Safe to print: inside a grip the assignment is constant by construction,
 * and it is read off the roll so it cannot contradict it. Outside any grip —
 * roughly 30% of attacks — the answer is empty and the caller writes nothing
 * rather than guessing.
 */
export function fingersAt(
  grips: readonly PhraseGrip[],
  tick: number,
  midi: number,
): readonly number[] {
  const grip = gripAt(grips, tick);

  if (grip === null) {
    return [];
  }

  return grip.fingers
    .filter((f) => f.midi === midi)
    .map((f) => f.finger)
    .sort((a, b) => a - b);
}

/**
 * Indices of the groups where the hand leaves everything it was holding.
 *
 * Computed against the grips rather than against the notes: two consecutive
 * grips with no key in common is the hand letting go and going somewhere
 * else, which is exactly the displacement this step exists to train. The
 * first grip of the run is not a move — the hand is already there.
 */
export function moveGroupIndices(
  groups: readonly PracticeGroup[],
  grips: readonly PhraseGrip[],
): ReadonlySet<number> {
  const out = new Set<number>();

  if (grips.length < 2) {
    return out;
  }

  for (let i = 1; i < grips.length; i += 1) {
    const previous = new Set(grips[i - 1].midis);
    const moved = grips[i].midis.every((m) => !previous.has(m));

    if (!moved) {
      continue;
    }

    const index = groups.findIndex((g) => g.startTick >= grips[i].startTick);

    if (index > 0) {
      out.add(index);
    }
  }

  return out;
}

/**
 * The place a hand's own grip step made it take at `tick`, or `null`.
 */
function executedPlaceAt(
  phrase: Phrase,
  hand: 'left' | 'right',
  tick: number,
): PhraseGrip | null {
  const plan = phraseHandShapePlan(phrase, hand);

  if (plan.kind !== 'shapes') {
    return null;
  }

  const place = shapePresses(plan)
    .map((press) => press.place)
    .find((p) => p.startTick <= tick && tick < p.endTick);

  return place ?? null;
}

/**
 * The line that opens a both-hands run, or `null`.
 *
 * There is not *one* configuration to hand back here — an earlier note in
 * this file concluded there was none, which was wrong. There are **two**, and
 * measured over 10 615 two-hand units they are both true at the first group
 * of the run **55.3%** of the time (78.1% among the units where both hands
 * got a shape plan), one of them a further 14.0%.
 *
 * So the rule is per hand and on confirmation: name the hands that really are
 * in a place their own step made them execute, and say nothing about the
 * others. Never a sentence that claims both when one is true.
 */
export function jointOpeningLine(
  phrase: Phrase,
  groups: readonly PracticeGroup[],
  pitchClassLabel: (pitchClass: number) => string,
): string | null {
  const first = groups[0];

  if (first === undefined) {
    return null;
  }

  const left = executedPlaceAt(phrase, 'left', first.startTick);
  const right = executedPlaceAt(phrase, 'right', first.startTick);
  const letters = (place: PhraseGrip) =>
    placeSpelling(place, pitchClassLabel).letters;

  if (left !== null && right !== null) {
    return `Reprends tes deux prises : main gauche ${letters(left)}, main droite ${letters(right)}.`;
  }

  if (left !== null) {
    return `Reprends ta prise main gauche : ${letters(left)}.`;
  }

  if (right !== null) {
    return `Reprends ta prise main droite : ${letters(right)}.`;
  }

  return null;
}

/**
 * What a screen reader is told about the current group.
 *
 * Names the hand before the keys, and says what the silent hand is doing —
 * in the step whose whole subject is which hand plays when. The sighted
 * player reads that from two colours and two lanes; without this the other
 * one got "Groupe 3 sur 12 : Do3, Mi4" and no way to tell them apart.
 */
export function groupAnnouncement(
  group: PracticeGroup,
  held: readonly HeldKey[],
  position: number,
  total: number,
  noteLabel: (midi: number) => string,
  appHand?: PracticeHand,
): string {
  const parts = [`Groupe ${position} sur ${total}.`];
  const sides: readonly (readonly [PracticeHand, string])[] = [
    ['L', 'Main gauche'],
    ['R', 'Main droite'],
  ];

  for (const [hand, word] of sides) {
    // During an accompanied pass this hand is not the player's, so neither
    // "you play" nor "you are holding" is true of it.
    if (hand === appHand) {
      parts.push(`${word} : l'application.`);
      continue;
    }

    const struck = group.notes.filter((n) => n.hand === hand);

    if (struck.length > 0) {
      parts.push(`${word} : ${struck.map((n) => noteLabel(n.midi)).join(', ')}.`);
      continue;
    }

    const holding = held.filter((k) => k.hand === hand);

    if (holding.length > 0) {
      parts.push(
        `${word} : tu tiens ${holding.map((k) => noteLabel(k.midi)).join(', ')}.`,
      );
    }
  }

  return parts.join(' ');
}

// ── The accompanied pass ──────────────────────────────────────────

/** One thing the application sounds: the keys of one instant. */
export type AccompanimentStep = {
  readonly midis: readonly number[];
};

export type AccompaniedRun = {
  /** The groups the player owns, restricted to their own hand. */
  readonly groups: readonly PracticeGroup[];
  /** What the application sounds before the player's first group. */
  readonly prelude: readonly AccompanimentStep[];
  /** What it sounds once the player's group `i` is validated. */
  readonly tails: readonly (readonly AccompanimentStep[])[];
};

/**
 * The run split between the player and the application, one hand each.
 *
 * The **sequence is not touched**: it stays exactly the one the score gives,
 * in the same order, at the same pitches, with nothing repeated. Only the
 * *responsibility* moves — the player owns every group containing a note of
 * their hand, and the application sounds the rest. That is what keeps the
 * step honest: an exercise that made someone play an order the music never
 * contains would be a motor lie, and this one never does.
 *
 * It is also why it is not the mono rung the run in time used to open on.
 * There the other hand was *removed*; here it is made audible, so the
 * interleaving — the 73.1% of this step's transitions that exist nowhere
 * else — stays intact. That rung is gone (see `mastery.ts`); this scaffold
 * is what was worth keeping of it.
 *
 * Measured cost of the split: the player keeps a median of 8 groups instead
 * of 12, and the bursts the application plays alone between two of theirs run
 * to a median of 1, P90 4.
 */
export function accompaniedRun(
  groups: readonly PracticeGroup[],
  playerHand: PracticeHand,
): AccompaniedRun {
  const playerGroups: PracticeGroup[] = [];
  const tails: AccompanimentStep[][] = [];
  const prelude: AccompanimentStep[] = [];

  for (const group of groups) {
    const mine = group.notes.filter((n) => n.hand === playerHand);
    const theirs = group.notes.filter((n) => n.hand !== playerHand);

    if (mine.length === 0) {
      // A group entirely the other hand's: it sounds after the player's
      // previous group, or before their first one.
      const step = { midis: theirs.map((n) => n.midi) };
      const target = tails.length === 0 ? prelude : tails[tails.length - 1];
      target.push(step);
      continue;
    }

    playerGroups.push({
      notes: mine,
      midis: mine.map((n) => n.midi),
      startTick: group.startTick,
    });

    // The other hand's share of the same instant sounds first, as the
    // player's keys go down — not after, which would turn a simultaneity
    // into an echo.
    tails.push(
      theirs.length > 0 ? [{ midis: theirs.map((n) => n.midi) }] : [],
    );
  }

  return { groups: playerGroups, prelude, tails };
}

// ── Sounding a fragment ───────────────────────────────────────────

/** One note the application is to sound, relative to the start of playback. */
export type ScheduledNote = {
  readonly midi: number;
  readonly atMs: number;
  readonly durMs: number;
};

/**
 * The fragment **in its real rhythm**, slowed by `rate`, for one hand or
 * both.
 *
 * The rhythm is the point. This is a *model* — "here is what you are
 * building" — and it is the first time in the journey that the fragment is
 * the music rather than a sequence of keys. Sounding its groups at a regular
 * interval instead would assert a rhythm the piece does not have: the same
 * motor lie as a discovery step that sorts notes by pitch, in audible form.
 *
 * `hand` is what the model is *of*, and it is not a detail. S0 plays the
 * unit whole, both hands, by itself, on arrival: an offer to hear both hands
 * again two screens later is the same recording answering a question the
 * player did not ask, while the thing they are about to play — this hand,
 * alone — has never been sounded anywhere in the journey. A model is only a
 * model of what is being built.
 */
export function auditionSchedule(
  phrase: Phrase,
  tickToSeconds: (tick: number) => number,
  rate: number,
  hand: PracticeHand | null = null,
): readonly ScheduledNote[] {
  const notes =
    hand === 'L'
      ? [...phrase.notesByHand.left]
      : hand === 'R'
        ? [...phrase.notesByHand.right]
        : [...phrase.notesByHand.left, ...phrase.notesByHand.right];

  if (notes.length === 0) {
    return [];
  }

  const origin = Math.min(...notes.map((n) => n.startTick));
  const zero = tickToSeconds(origin);
  const at = (tick: number) => ((tickToSeconds(tick) - zero) / rate) * 1000;

  return notes
    .map((n) => ({
      midi: n.midi,
      atMs: at(n.startTick),
      durMs: Math.max(MIN_SOUNDED_MS, at(n.endTick) - at(n.startTick)),
    }))
    .sort((a, b) => a.atMs - b.atMs || a.midi - b.midi);
}

/**
 * The application's share of an accompanied pass, at a **neutral spacing**.
 *
 * Deliberately not the real rhythm, and the difference with `auditionSchedule`
 * is not an inconsistency. A model claims "this is the piece", so it has to
 * be true in time. An accompaniment inside the no-rhythm block claims nothing
 * about duration — the player is not playing in time either — so a regular
 * spacing adds no assertion the step was not already making.
 */
export function accompanimentSchedule(
  steps: readonly AccompanimentStep[],
  stepMs: number,
  holdMs: number,
): readonly ScheduledNote[] {
  return steps.flatMap((step, index) =>
    step.midis.map((midi) => ({
      midi,
      atMs: index * stepMs,
      durMs: holdMs,
    })),
  );
}

/** Floor on a sounded note, so a grace note is still audible. */
const MIN_SOUNDED_MS = 90;

// ── Diagnosis of a wrong key ──────────────────────────────────────

const NEIGHBOUR_WORD: Readonly<Record<number, string>> = {
  1: 'un demi-ton',
  2: 'un ton',
};

/**
 * What to say about a key that was not in the group, or `null`.
 *
 * Every branch is arithmetic on two integers — never an interpretation — so
 * the rule that governs the whole journey holds: when we are not sure, we say
 * nothing. Replacing a running tally of mistakes, which named nothing, gave
 * no direction, and was the only thing the step said about failing.
 *
 * Order is by certainty, not by frequency. The neighbour gives a direction,
 * which keeps the search a search. Anticipation comes last and only when the
 * key cannot also be read as a near miss: otherwise every fumble onto the
 * next note would be called a jump ahead, which is an interpretation and not
 * a fact.
 *
 * No octave line any more. « C'est la bonne note, une octave trop bas » named
 * the key by a relation the player had to translate before moving, on a step
 * whose whole panel has just been reduced to « À toi de jouer ».
 */
export function pressDiagnosis(
  played: number,
  expected: readonly number[],
  next: readonly number[],
  held: ReadonlySet<number>,
  noteLabel: (midi: number) => string,
): string | null {
  // First, because it is the most specific fact available and because it is
  // the characteristic mistake of playing hands together: re-striking a note
  // that was supposed to be held. It produced silence before — none of the
  // arithmetic branches below matches it — in a step where 39.9% of
  // groups ask one hand to hold while the other plays.
  if (held.has(played)) {
    return 'Elle sonne déjà — garde le doigt dessus.';
  }

  if (expected.length === 0) {
    return null;
  }

  const closest = expected.reduce((best, midi) =>
    Math.abs(midi - played) < Math.abs(best - played) ? midi : best,
  );
  const distance = Math.abs(closest - played);
  const word = NEIGHBOUR_WORD[distance];

  if (word !== undefined) {
    return `Tu es juste à côté de ${noteLabel(closest)} : cherche ${word} plus ${played < closest ? 'haut' : 'bas'}.`;
  }

  if (next.includes(played)) {
    return 'Tu as anticipé : celle-là vient juste après.';
  }

  return null;
}
